"use strict";

theme.AjaxRenderer = (function () {
  function AjaxRenderer({ sections, onReplace, debug } = {}) {
    this.sections = sections || [];
    this.cachedSections = [];
    this.onReplace = onReplace;
    this.debug = Boolean(debug);
  }

  AjaxRenderer.prototype = Object.assign({}, AjaxRenderer.prototype, {
    renderPage: function (basePath, newParams, updateURLHash = true) {
      const currentParams = new URLSearchParams(window.location.search);
      const updatedParams = this.getUpdatedParams(currentParams, newParams);

      const sectionRenders = this.sections.map((section) => {
        const url = `${basePath}?section_id=${section.sectionId}&${updatedParams.toString()}`;
        const cachedSectionUrl = (cachedSection) => cachedSection.url === url;

        return this.cachedSections.some(cachedSectionUrl)
          ? this.renderSectionFromCache(cachedSectionUrl, section)
          : this.renderSectionFromFetch(url, section);
      });

      if (updateURLHash) this.updateURLHash(updatedParams);

      return Promise.all(sectionRenders);
    },

    renderSectionFromCache: function (url, section) {
      const cachedSection = this.cachedSections.find(url);

      this.log(
        `[AjaxRenderer] rendering from cache: url=${cachedSection.url}`,
      );
      this.renderSection(cachedSection.html, section);
      return Promise.resolve(section);
    },

    renderSectionFromFetch: function (url, section) {
      this.log(`[AjaxRenderer] redering from fetch: url=${url}`);

      return new Promise((resolve, reject) => {
        fetch(url)
          .then((response) => response.text())
          .then((responseText) => {
            const html = responseText;
            this.cachedSections = [...this.cachedSections, { html, url }];
            this.renderSection(html, section);
            resolve(section);
          })
          .catch((err) => reject(err));
      });
    },

    renderSection: function (html, section) {
      this.log(
        `[AjaxRenderer] rendering section: section=${JSON.stringify(section)}`,
      );

      const newDom = new DOMParser().parseFromString(html, "text/html");
      if (this.onReplace) {
        this.onReplace(newDom, section);
      } else {
        if (typeof section.nodeId === "string") {
          var newContentEl = newDom.getElementById(section.nodeId);
          if (!newContentEl) {
            return;
          }

          document.getElementById(section.nodeId).innerHTML =
            newContentEl.innerHTML;
        } else {
          section.nodeId.forEach((id) => {
            document.getElementById(id).innerHTML =
              newDom.getElementById(id).innerHTML;
          });
        }
      }

      return section;
    },

    getUpdatedParams: function (currentParams, newParams) {
      const clone = new URLSearchParams(currentParams);
      const preservedParams = ["sort_by", "q", "options[prefix]", "type"];

      // Find what params need to be removed
      // delete happens first as we cannot specify keys based off of values
      for (const [key, value] of clone.entries()) {
        if (
          !newParams.getAll(key).includes(value) &&
          !preservedParams.includes(key)
        ) {
          clone.delete(key);
        }
      }

      // Find what params need to be added
      for (const [key, value] of newParams.entries()) {
        if (!clone.getAll(key).includes(value) && value !== "") {
          clone.append(key, value);
        }
      }

      return clone;
    },

    updateURLHash: function (searchParams) {
      history.pushState(
        {},
        "",
        `${window.location.pathname}${
          searchParams && "?".concat(searchParams)
        }`,
      );
    },

    log: function (...args) {
      if (this.debug) {
        console.log(...args);
      }
    },
  });

  return AjaxRenderer;
})();
theme.cart = {
  getCart: function () {
    var url = "".concat(theme.routes.cart, "?t=").concat(Date.now());
    return fetch(url, {
      credentials: "same-origin",
      method: "GET",
    }).then((response) => response.json());
  },

  getCartProductMarkup: function () {
    var url = "".concat(theme.routes.cartPage, "?t=").concat(Date.now());

    url = url.indexOf("?") === -1 ? url + "?view=ajax" : url + "&view=ajax";

    return fetch(url, {
      credentials: "same-origin",
      method: "GET",
    }).then(function (response) {
      return response.text();
    });
  },

  changeItem: function (key, qty) {
    return this._updateCart({
      url: "".concat(theme.routes.cartChange, "?t=").concat(Date.now()),
      data: JSON.stringify({
        id: key,
        quantity: qty,
      }),
    });
  },

  _updateCart: function (params) {
    return fetch(params.url, {
      method: "POST",
      body: params.data,
      credentials: "same-origin",
      headers: {
        "Content-Type": "application/json",
        "X-Requested-With": "XMLHttpRequest",
      },
    })
      .then((response) => response.json())
      .then(function (cart) {
        return cart;
      });
  },

  updateAttribute: function (key, value) {
    return this._updateCart({
      url: "/cart/update.js",
      data: JSON.stringify({
        attributes: {
          [key]: theme.cart.attributeToString(value),
        },
      }),
    });
  },

  updateNote: function (note) {
    return this._updateCart({
      url: "/cart/update.js",
      data: JSON.stringify({
        note: theme.cart.attributeToString(note),
      }),
    });
  },

  attributeToString: function (attribute) {
    if (typeof attribute !== "string") {
      attribute += "";
      if (attribute === "undefined") {
        attribute = "";
      }
    }
    return attribute.trim();
  },
};

/*============================================================================
  CartForm
  - Prevent checkout when terms checkbox exists
  - Listen to quantity changes, rebuild cart (both widget and page)
==============================================================================*/
theme.CartForm = (function () {
  var selectors = {
    products: "[data-products]",
    qtySelector: ".js-qty__wrapper",
    discounts: "[data-discounts]",
    savings: "[data-savings]",
    subTotal: "[data-subtotal]",

    cartBubble: ".cart-link__bubble",
    cartNote: '[name="note"]',
    termsCheckbox: ".cart__terms-checkbox",
    checkoutBtn: ".cart__checkout",
  };

  var classes = {
    btnLoading: "btn--loading",
  };

  var config = {
    requiresTerms: false,
  };

  function CartForm(form) {
    if (!form) {
      return;
    }

    this.form = form;
    this.wrapper = form.parentNode;
    this.location = form.dataset.location;
    this.namespace = ".cart-" + this.location;
    this.products = form.querySelector(selectors.products);
    this.submitBtn = form.querySelector(selectors.checkoutBtn);

    this.discounts = form.querySelector(selectors.discounts);
    this.savings = form.querySelector(selectors.savings);
    this.subtotal = form.querySelector(selectors.subTotal);
    this.termsCheckbox = form.querySelector(selectors.termsCheckbox);
    this.noteInput = form.querySelector(selectors.cartNote);

    if (this.termsCheckbox) {
      config.requiresTerms = true;
    }

    this.init();
  }

  CartForm.prototype = Object.assign({}, CartForm.prototype, {
    init: function () {
      this.initQtySelectors();

      document.addEventListener(
        "cart:quantity" + this.namespace,
        this.quantityChanged.bind(this),
      );

      this.form.on("submit" + this.namespace, this.onSubmit.bind(this));

      if (this.noteInput) {
        this.noteInput.addEventListener("change", function () {
          var newNote = this.value;
          theme.cart.updateNote(newNote);
        });
      }

      // Dev-friendly way to build the cart
      document.addEventListener(
        "cart:build",
        function () {
          this.buildCart();
        }.bind(this),
      );
    },

    reInit: function () {
      this.initQtySelectors();
      theme.lazyImages();
    },

    onSubmit: function (evt) {
      this.submitBtn.classList.add(classes.btnLoading);

      if (config.requiresTerms) {
        if (this.termsCheckbox.checked) {
          // continue to checkout
        } else {
          alert(theme.strings.cartTermsConfirmation);
          this.submitBtn.classList.remove(classes.btnLoading);
          evt.preventDefault();
          return false;
        }
      }
    },

    /*============================================================================
      Query cart page to get markup
    ==============================================================================*/
    _parseProductHTML: function (html) {
      var parser = new DOMParser();
      var doc = parser.parseFromString(html, "text/html");
      return {
        items: doc.querySelector(".cart__items"),
        discounts: doc.querySelector(".cart__discounts"),
      };
    },

    buildCart: function () {
      theme.cart.getCartProductMarkup().then(this.cartMarkup.bind(this));
    },

    cartMarkup: function (html) {
      var markup = this._parseProductHTML(html);
      var items = markup.items;
      var count = parseInt(items.dataset.count);
      var subtotal = items.dataset.cartSubtotal;
      var savings = items.dataset.cartSavings;

      this.updateCartDiscounts(markup.discounts);
      this.updateSavings(savings);

      if (count > 0) {
        this.wrapper.classList.remove("is-empty");
      } else {
        this.wrapper.classList.add("is-empty");
      }

      this.updateCount(count);

      // Append item markup
      this.products.innerHTML = "";
      this.products.append(items);

      // Update subtotal
      this.subtotal.innerHTML = theme.Currency.formatMoney(
        subtotal,
        theme.settings.moneyFormat,
      );

      this.reInit();

      if (window.AOS) {
        AOS.refreshHard();
      }

      if (Shopify && Shopify.StorefrontExpressButtons) {
        Shopify.StorefrontExpressButtons.initialize();
      }
    },

    updateCartDiscounts: function (markup) {
      if (!this.discounts) {
        return;
      }
      this.discounts.innerHTML = "";
      this.discounts.append(markup);
    },

    /*============================================================================
      Quantity handling
    ==============================================================================*/
    initQtySelectors: function () {
      this.form.querySelectorAll(selectors.qtySelector).forEach((el) => {
        var selector = new theme.QtySelector(el, {
          namespace: this.namespace,
          isCart: true,
        });
      });
    },

    quantityChanged: function (evt) {
      var key = evt.detail[0];
      var qty = evt.detail[1];
      var el = evt.detail[2];

      if (!key || !qty) {
        return;
      }

      // Disable qty selector so multiple clicks can't happen while loading
      if (el) {
        el.classList.add("is-loading");
      }

      theme.cart
        .changeItem(key, qty)
        .then(
          function (cart) {
            if (cart.item_count > 0) {
              this.wrapper.classList.remove("is-empty");
            } else {
              this.wrapper.classList.add("is-empty");
            }

            this.buildCart();

            document.dispatchEvent(
              new CustomEvent("cart:updated", {
                detail: {
                  cart: cart,
                },
              }),
            );
          }.bind(this),
        )
        .catch(function (XMLHttpRequest) {});
    },

    /*============================================================================
      Update elements of the cart
    ==============================================================================*/
    updateSubtotal: function (subtotal) {
      this.form.querySelector(selectors.subTotal).innerHTML =
        theme.Currency.formatMoney(subtotal, theme.settings.moneyFormat);
    },

    updateSavings: function (savings) {
      if (!this.savings) {
        return;
      }

      if (savings > 0) {
        var amount = theme.Currency.formatMoney(
          savings,
          theme.settings.moneyFormat,
        );
        this.savings.classList.remove("hide");
        this.savings.innerHTML = theme.strings.cartSavings.replace(
          "[savings]",
          amount,
        );
      } else {
        this.savings.classList.add("hide");
      }
    },

    updateCount: function (count) {
      var countEls = document.querySelectorAll(".cart-link__bubble-num");

      if (countEls.length) {
        countEls.forEach((el) => {
          el.innerText = count;
        });
      }

      // show/hide bubble(s)
      var bubbles = document.querySelectorAll(selectors.cartBubble);
      if (bubbles.length) {
        if (count > 0) {
          bubbles.forEach((b) => {
            b.classList.add("cart-link__bubble--visible");
          });
        } else {
          bubbles.forEach((b) => {
            b.classList.remove("cart-link__bubble--visible");
          });
        }
      }
    },
  });

  return CartForm;
})();

// Either collapsible containers all acting individually,
// or tabs that can only have one open at a time
theme.CartDrawer = (function () {
  var selectors = {
    drawer: "#CartDrawer",
    form: "#CartDrawerForm",
  };

  function CartDrawer() {
    this.form = document.querySelector(selectors.form);
    this.drawer = new theme.Drawers("CartDrawer", "cart");

    this.init();
  }

  CartDrawer.prototype = Object.assign({}, CartDrawer.prototype, {
    init: function () {
      this.cartForm = new theme.CartForm(this.form);
      this.cartForm.buildCart();

      document.addEventListener(
        "ajaxProduct:added",
        function (evt) {
          this.cartForm.buildCart();
          this.open();
        }.bind(this),
      );

      // Dev-friendly way to open cart
      document.addEventListener("cart:open", this.open.bind(this));
      document.addEventListener("cart:close", this.close.bind(this));
    },

    open: function () {
      this.drawer.open();
    },

    close: function () {
      this.drawer.close();
    },
  });

  return CartDrawer;
})();

window.onpageshow = function (evt) {
  // Removes unload class when returning to page via history
  if (evt.persisted) {
    document.body.classList.remove("unloading");
    document.querySelectorAll(".cart__checkout").forEach((el) => {
      el.classList.remove("btn--loading");
    });
  }
};

